<template>
  <div class="crud_form_wrapper single_show_content_wrapper">
    <!-- Start:: Title -->
    <div class="form_title_wrapper">
      <h4>{{ $t("TITLES.showClient", { name: data.user_name }) }}</h4>
      <v-chip
        :color="data.user_type === 'provider' ? 'blue' : 'green'"
        text-color="white"
        class="ml-3"
      >
        {{ data.user_type_label }}
      </v-chip>
    </div>
    <div class="col-12 text-end">
      <v-btn @click="$router.go(-1)" style="color: #26a1d6">
        <i class="fas fa-backward"></i>
      </v-btn>
    </div>
    <!-- End:: Title -->

    <!-- Start:: Single Step Form Content -->
    <div class="single_step_form_content_wrapper">
      <!-- ==== Start:: Status Badges ==== -->
      <div class="badges_wrapper justify-content-between mb-4">
        <div class="wrapper">
          <v-chip color="amber darken-2" text-color="white" class="mr-2">
            {{ $t("PLACEHOLDERS.joining_date") }}: {{ data.created_at }}
          </v-chip>
          <v-chip
            v-if="data.user_type === 'provider'"
            color="blue darken-2"
            text-color="white"
            class="mr-2"
          >
            {{ $t("PLACEHOLDERS.total_ads") }}: {{ data.total_ads || 0 }}
          </v-chip>
          <v-chip
            v-if="data.user_type === 'provider'"
            color="green darken-2"
            text-color="white"
            class="mr-2"
          >
            {{ $t("PLACEHOLDERS.active_ads") }}: {{ data.active_ads || 0 }}
          </v-chip>
          <v-chip
            v-if="data.user_type === 'client'"
            color="purple darken-2"
            text-color="white"
            class="mr-2"
          >
            {{ $t("PLACEHOLDERS.rental_requests_sent") }}:
            {{ data.rental_requests_sent || 0 }}
          </v-chip>
          <v-chip
            v-if="data.user_type === 'client'"
            color="teal darken-2"
            text-color="white"
            class="mr-2"
          >
            {{ $t("PLACEHOLDERS.accepted_requests") }}:
            {{ data.accepted_requests || 0 }}
          </v-chip>
        </div>

        <v-chip :color="data.is_active ? 'green' : 'red'" text-color="white">
          {{ data.is_active ? $t("STATUS.active") : $t("STATUS.notActive") }}
        </v-chip>
      </div>
      <!-- ==== End:: Status Badges ==== -->

      <!-- ==== Start:: Client Main Data ==== -->
      <form>
        <div class="row">
          <!-- Start:: Image Upload Input -->
          <base-image-upload-input
            col="12"
            identifier="userImage"
            :preSelectedImage="data.image.path"
            :placeholder="$t('PLACEHOLDERS.userImage')"
            disabled
          />
          <!-- End:: Image Upload Input -->

          <!-- Start:: Basic Info -->
          <base-input
            col="4"
            type="text"
            :placeholder="$t('PLACEHOLDERS.user_name')"
            v-model.trim="data.user_name"
            disabled
            class="disabled_input"
          />

          <base-input
            col="4"
            type="text"
            :placeholder="$t('PLACEHOLDERS.phone')"
            v-model.trim="data.phone"
            disabled
            class="disabled_input"
          />

          <base-input
            col="4"
            type="email"
            :placeholder="$t('PLACEHOLDERS.email')"
            v-model.trim="data.email"
            readonly
            class="disabled_input"
          />
          <!-- End:: Basic Info -->

          <!-- Start:: National ID -->
          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.national_id')"
            v-model.trim="data.national_id"
            disabled
            class="disabled_input"
          />

          <!-- Start:: Landlord Specific Fields -->
          <template v-if="data.user_type === 'provider'">
            <base-input
              col="6"
              type="text"
              :placeholder="$t('PLACEHOLDERS.landlord_type')"
              v-model.trim="data._landlord_type"
              disabled
              class="disabled_input"
            />

            <base-input
              v-if="data.landlord_type === 'employee'"
              col="4"
              type="text"
              :placeholder="$t('PLACEHOLDERS.job_title')"
              v-model.trim="data.job_title"
              disabled
              class="disabled_input"
            />

            <base-input
              col="4"
              type="text"
              :placeholder="$t('PLACEHOLDERS.package_name')"
              v-model.trim="data.package_name"
              disabled
              class="disabled_input"
            />

            <base-input
              col="4"
              type="text"
              :placeholder="$t('PLACEHOLDERS.remaining_ads')"
              v-model.trim="data.remaining_ads"
              disabled
              class="disabled_input"
            />
          </template>
          <!-- End:: Landlord Specific Fields -->

          <!-- Start:: Tenant Specific Fields -->
          <template v-if="data.user_type === 'client'">
            <base-image-upload-input
              col="6"
              identifier="commercial_number_image"
              :preSelectedImage="data.commercial_number_image.path"
              :placeholder="$t('PLACEHOLDERS.commercial_register')"
              disabled
            />

            <!-- Work File PDF Display -->
            <div class="col-6">
              <label class="input_label">{{
                $t("PLACEHOLDERS.work_file")
              }}</label>
              <div class="file_display_wrapper mt-2">
                <a
                  v-if="data.work_file_pdf.path"
                  :href="data.work_file_pdf.path"
                  target="_blank"
                  class="btn btn-sm"
                >
                  <i class="fas fa-file-pdf"></i>
                  {{ $t("BUTTONS.view_work_file") }}
                </a>
                <p v-else class="text-muted">{{ $t("TABLES.noData") }}</p>
              </div>
            </div>
          </template>
          <!-- End:: Tenant Specific Fields -->
        </div>
      </form>
      <!-- ==== End:: Client Main Data ==== -->
    </div>
    <!-- End:: Single Step Form Content -->
  </div>
</template>

<script>
export default {
  name: "SingleClient",

  props: {
    id: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      // Start:: Loading Data
      isWaitingRequest: false,
      // End:: Loading Data

      // Start:: Table Data
      addressesTableHeaders: [
        { text: this.$t("TABLES.Addresses.serialNumber") },
        { text: this.$t("TABLES.Addresses.address") },
        { text: this.$t("TABLES.Addresses.longitude") },
        { text: this.$t("TABLES.Addresses.latitude") },
        { text: this.$t("TABLES.Addresses.type") },
        { text: this.$t("TABLES.Addresses.isDefault") },
      ],
      // End:: Table Data

      // Start:: Data
      data: {
        image: {
          path: null,
          file: null,
        },
        commercial_number_image: {
          path: null,
          file: null,
        },
        work_file_pdf: {
          path: null,
          file: null,
        },
        user_name: null,
        phone: null,
        email: null,
        national_id: null,
        user_type: null,
        user_type_label: null,
        is_active: false,
        created_at: null,

        // Landlord specific
        landlord_type: null,
        _landlord_type: null,
        job_title: null,
        total_ads: 0,
        active_ads: 0,
        package_name: null,
        remaining_ads: 0,

        // Tenant specific
        rental_requests_sent: 0,
        accepted_requests: 0,
      },
      // End:: Data
    };
  },

  methods: {
    // Start:: Get Data To Show
    async getDataToShow() {
      try {
        let res = await this.$axios({
          method: "GET",
          url: `clients/${this.$route.params.id}`,
        });

        // Handle both wrapped and direct user object
        const userData =
          res.data.data?.user || res.data.data?.client?.user || res.data.data;

        // Basic user info
        this.data.image.path = userData.avatar;
        this.data.user_name = userData.name;
        this.data.phone = userData.mobile;
        this.data.email = userData.email;
        this.data.national_id = userData.national_id;
        this.data.is_active = userData.is_active;
        this.data.created_at = userData.created_at;

        // Set user type and label
        this.data.user_type = userData.type;
        this.data.user_type_label =
          userData._type ||
          (userData.type === "landlord"
            ? this.$t("PLACEHOLDERS.landlord")
            : this.$t("PLACEHOLDERS.tenant"));

        // Tenant specific data
        if (this.data.user_type === "client") {
          this.data.commercial_number_image.path =
            userData.commercial_number_image;
          this.data.work_file_pdf.path = userData.work_file_pdf;

          // Get tenant statistics (these would come from API or separate calls)
          this.data.rental_requests_sent = userData.rental_requests_sent || 0;
          this.data.accepted_requests = userData.accepted_requests || 0;
        }

        // Landlord specific data
        if (this.data.user_type === "provider") {
          this.data._landlord_type =
            userData._landlord_type ||
            this.$t("PLACEHOLDERS.individual_landlord");

          // Get landlord statistics (these would come from API or separate calls)
          this.data.landlord_type = userData.landlord_type;
          this.data.job_title = userData.job_title;
          this.data.total_ads = userData.total_ads || 0;
          this.data.active_ads = userData.active_ads || 0;
          this.data.package_name =
            userData.package_name || this.$t("PLACEHOLDERS.no_package");
          this.data.remaining_ads = userData.remaining_ads || 0;
        }
      } catch (error) {
        console.log(error.response.data.message);
      }
    },
    // End:: Get Data To Show
  },

  created() {
    // Start:: Fire Methods
    this.getDataToShow();
    // End:: Fire Methods
  },
};
</script>
<style>
.preview-container img {
  max-width: 100%;
  max-height: 200px;
  border: 1px solid #ccc;
  border-radius: 15px;
}
</style>
